import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { getUserDisplayName } from '../../../utils/name_utils';

import Avatar from '../../common/Avatar/Avatar';
import CardHeader from '../../common/Labels/CardHeader';
import Label from '../../common/Labels/Label';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';
import useUserContext from '../../contexts/UserData/UserData';

const WorkspaceItem = ({
  workspaceId,
  name,
  owner,
  onClick,
  height = 128,
  selected = false,
}) => {
  const hasOnClick = isFunction(onClick);
  const handleClick = useCallback(() => {
    if (hasOnClick) {
      onClick(selected ? null : workspaceId)
    }
  }, [hasOnClick, onClick, selected, workspaceId])

  return (
    <button
      id={`workspace-item-${name}`}
      type="button"
      className={classNames('p-4 w-full border rounded flex flex-col',
        { 'cursor-pointer': hasOnClick },
        { 'bg-white': !selected },
        { 'hover:bg-border-light': hasOnClick && !selected },
        { 'bg-border-dark': selected })}
      onClick={handleClick}
      style={{ height: `${height}px` }}
    >
      <CardHeader className="w-full">{name}</CardHeader>
      <div className="ml-2 mt-1 flex items-center">
        <Label className="mr-2" bold>Owner: </Label>
        <Avatar />
        <Label className="ml-2">{getUserDisplayName(owner)}</Label>
      </div>
    </button>
  );
}

const ListRow = ({ 
  index,
  style,
  data
}) => {
  const {
    workspaces,
    selectedWorkspaceId,
    onWorkspaceClick,
    userData,
    listRef,
    rowHeights
  } = data;
  const rowRef = useRef({});
  const workspace = workspaces?.length ? workspaces[index] : null;
  const workspaceId = workspace?.id;
  const workspaceName = workspace?.name.endsWith(' - Default Workspace') && workspace?.createdBy === userData?.id
    ? 'My Default Workspace'
    : workspace?.name;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  const hasClickFunction = onWorkspaceClick && isFunction(onWorkspaceClick);
  const onClick = useCallback(() => {
    if (hasClickFunction) {
      onWorkspaceClick(workspaceId, index);
    }
  }, [hasClickFunction, workspaceId, index, onWorkspaceClick])

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div style={style}>
      <WorkspaceItem
        key={workspace.id}
        workspaceId={workspace.id}
        name={workspaceName}
        owner={workspace?.owner}
        selected={selectedWorkspaceId === workspace.id}
        onClick={onClick}
      />
    </div>
  )
}

const VirtualizedListSection = ({ 
  width,
  workspaces,
  selectedWorkspaceId,
  onWorkspaceClick, 
  height,
  workspaceItemSize,
  userData,
  listRef,
  getRowHeight,
  rowHeights
 }) => {
  if (!workspaces?.length) {
    return null;
  }

  return (
    <VariableSizeList
      ref={listRef}
      height={height}
      itemCount={workspaces?.length || 0}
      estimatedItemSize={workspaceItemSize}
      itemSize={getRowHeight}
      width={width}
      itemData={{
        workspaces,
        selectedWorkspaceId,
        onWorkspaceClick,
        userData,
        listRef,
        rowHeights
      }}
    >
      {ListRow}
    </VariableSizeList>
  )
}

export const WorkspaceList = ({
  className,
  workspaces = [],
  selectedWorkspaceId,
  onWorkspaceClick,
  height = 294,
  workspaceItemSize = 138,
  disableScrollFading = false,
}) => {
  const { userData } = useUserContext()
  const listRef = useRef({});
  const rowHeights = useRef({});

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < workspaceItemSize) {
      return workspaceItemSize;
    }
    return rowHeight || workspaceItemSize;
  }, [workspaceItemSize, rowHeights]);

  return (
    <div className="relative">
      <div id="workspace-list-scroll-box" className={classNames('overflow-scroll py-4 flex-grow', className)}>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
          )
        }
        <AutoSizer disableHeight>
          {({ width }) => (
            <VirtualizedListSection
              width={width}
              workspaces={workspaces}
              selectedWorkspaceId={selectedWorkspaceId}
              onWorkspaceClick={onWorkspaceClick}
              height={height}
              workspaceItemSize={workspaceItemSize}
              userData={userData}
              listRef={listRef}
              getRowHeight={getRowHeight}
              rowHeights={rowHeights}
            />
          )}
        </AutoSizer>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          )
        }
      </div>
    </div>
  )
}

export default WorkspaceList;
